import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, WifiOff, Wrench } from 'lucide-react';
import Logo from './Logo';
import { brandLogoTexts, readCachedBrand } from '../lib/brand';
import { useSettings } from '../context/SettingsContext';

/**
 * Sunucuya hiç ulaşılamadığında portalın yerine çizilen tam ekran duyuru.
 *
 * Kod güncellemesi sırasında backend servisi yeniden başlar (bkz.
 * deploy/update.sh) ve açılış işleri bitene kadar ters vekil 502/503 döner.
 * O saniyelerde site açan oyuncu, ayarları hiç gelmediği için yarım bir kabuk
 * ya da tarayıcının/vekilin kendi hata sayfasını görüyordu — ikisi de "site
 * kapandı" gibi okunur. Bunun yerine ne olduğunu söyleyen bir ekran çizilir:
 * sayfa açık kaldığı sürece bağlantı arka planda yoklanır
 * (SettingsContext → UNREACHABLE_RETRY_MS) ve sunucu döndüğü anda site
 * kendiliğinden açılır; oyuncudan sayfayı yenilemesi istenmez.
 *
 * Kilit ekranından (SiteLockedScreen) farkı gerekçedir: orada hizmet bilerek
 * kapatılmıştır, burada geçici bir kesinti vardır.
 */

// Güncelleme genelde bu kadar sürer: servis yenilenir, açılış işleri (migration,
// uzlaştırma) biter ve sağlık kontrolü geçer. Süre bir ölçü değil vaattir;
// sayaç bunu aşınca metin değişir — tutulmayan bir söz, sözsüzlükten kötüdür.
export const EXPECTED_SECONDS = 30;

const ServiceUnavailableScreen = () => {
  const settings = useSettings();
  const { refreshSettings } = settings;
  const [elapsed, setElapsed] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  // Marka ayarlardan gelemez (elde ayar yok); son bilinen değer okunur —
  // açılış ekranıyla aynı önbellek. Adres orada çözülmüş hâlde durur.
  const cachedBrand = useMemo(() => readCachedBrand(), []);
  const logoImage = !logoFailed ? cachedBrand.logo_image_url : '';
  const { primary } = brandLogoTexts(settings);
  // Aynı ekran, oyuncunun kendi bağlantısı koptuğunda da çıkar. "Güncelleme
  // yapıyoruz" demek orada yanlış bilgi olur; durum tarayıcıdan okunur.
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );

  useEffect(() => {
    const timer = window.setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const online = () => setOffline(false);
    const gone = () => setOffline(true);
    window.addEventListener('online', online);
    window.addEventListener('offline', gone);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', gone);
    };
  }, []);

  const retryNow = useCallback(async () => {
    setRetrying(true);
    try {
      await refreshSettings?.();
    } finally {
      // Yanıt anında gelse bile göstergenin bir kare görünmesi gerekir; aksi
      // hâlde düğme hiçbir şey yapmamış gibi durur.
      window.setTimeout(() => setRetrying(false), 600);
    }
  }, [refreshSettings]);

  const takingLonger = elapsed > EXPECTED_SECONDS;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[var(--pusu-bg-deep)]">
      <main className="min-h-full grid place-items-center px-4 py-10">
        <section className="pusu-panel w-full max-w-lg p-8 text-center">
          {/* Marka hiç bilinmiyorsa (ilk ziyaret, temizlenmiş depolama) logo
              alanı çizilmez: Logo bileşeninin yer tutucusu "birazdan dolacak"
              demektir, oysa bu ekranda dolacağı bir ayar yoktur. */}
          {logoImage ? (
            <div className="flex justify-center mb-6">
              <img
                src={logoImage}
                alt=""
                className="h-12 w-auto max-w-[220px] object-contain"
                onError={() => setLogoFailed(true)}
              />
            </div>
          ) : primary ? (
            <div className="flex justify-center mb-6">
              <Logo size="lg" linked={false} />
            </div>
          ) : null}

          <div
            className="mx-auto mb-5 grid place-items-center w-14 h-14 rounded-2xl bg-white/5 border border-white/10"
            aria-hidden="true"
          >
            {offline
              ? <WifiOff size={24} className="text-[var(--pusu-gold)]" />
              : <Wrench size={24} className="text-[var(--pusu-gold)]" />}
          </div>

          {offline ? (
            <>
              <h1 className="text-2xl font-black mb-3">İnternet bağlantısı yok</h1>
              <p className="text-sm text-gray-400 leading-relaxed">
                Cihazınız şu anda çevrimdışı görünüyor. Bağlantınız geri
                geldiğinde bu sayfa kendiliğinden açılacak.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-black mb-3">Kısa bir güncelleme yapıyoruz</h1>
              <p className="text-sm text-gray-400 leading-relaxed">
                {takingLonger
                  ? 'Güncelleme beklenenden biraz uzun sürüyor. Sayfa hazır olduğunda kendiliğinden açılacak; kapatmanıza gerek yok.'
                  : `Site şu anda güncelleniyor ve yaklaşık ${EXPECTED_SECONDS} saniye içinde yeniden açılacak. Bu sayfayı kapatmanıza gerek yok, hazır olduğunda kendiliğinden devam edecek.`}
              </p>
              <p className="text-sm text-gray-500 leading-relaxed mt-3">
                Bakiyeniz, kuponlarınız ve hesap bilgileriniz güvende.
              </p>
            </>
          )}

          {/* Bekleme çubuğu: ekranın donmadığını, arka planda bağlantının
              denendiğini gösterir (açılış ekranındaki çubuğun aynısı). */}
          <div className="mt-7 h-1 w-full overflow-hidden rounded-full bg-white/10" aria-hidden="true">
            <div className="service-wait-bar h-full w-2/5 rounded-full bg-[var(--pusu-gold)]" />
          </div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={retryNow}
              disabled={retrying}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-white/10 text-gray-300 hover:text-white hover:border-white/25 disabled:opacity-60"
            >
              <RefreshCw size={15} className={retrying ? 'animate-spin' : ''} />
              {retrying ? 'Deneniyor…' : 'Şimdi dene'}
            </button>
            <p className="text-xs text-gray-600" aria-live="polite">
              Bağlantı arka planda deneniyor · {elapsed} sn
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ServiceUnavailableScreen;
